// Pending returns (decision 064, F420; F206): what is packed and waiting
// to be sent, and what the platform could not take as it came, held for
// the owner. Under the host alone.

import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { NotOnThisPc } from "@/components/NotOnThisPc";
import { formatBytes } from "@/host/format";
import { useHost } from "@/host/host";
import { hostQueries } from "@/host/queries";
import type { OutboxRow } from "@/host/types";

export const Route = createFileRoute("/pc/returns")({
  component: PendingReturns,
});

function PendingReturns() {
  const host = useHost();
  if (!host) return <NotOnThisPc />;
  return <PendingReturnsUnderHost />;
}

function PendingReturnsUnderHost() {
  const host = useHost()!;
  const outbox = useQuery(hostQueries.outbox(host));
  if (outbox.isPending) return <p className="muted">Reading the pending returns...</p>;
  if (outbox.isError) return <p className="alert">{String(outbox.error)}</p>;
  const { pending, conflicts } = outbox.data;
  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Returns waiting to be sent</h1>
        <p className="muted text-sm">A return is packed here before anything is sent. If the platform does not answer, the state waits and the next return resumes it. A state the platform could not take as it came waits for the owner to accept.</p>
      </header>
      <section className="card" data-testid="returns-waiting">
        <h2 className="text-[1.0714rem] font-semibold">Waiting to be sent ({pending.length})</h2>
        {pending.length === 0 ? <p className="muted mt-1 text-xs">Nothing waiting.</p> : pending.map((e) => <Entry key={e.id} entry={e} />)}
      </section>
      <section className="card" data-testid="returns-held">
        <h2 className="text-[1.0714rem] font-semibold">Waiting for the owner ({conflicts.length})</h2>
        {conflicts.length === 0 ? <p className="muted mt-1 text-xs">None.</p> : conflicts.map((e) => <Entry key={e.id} entry={e} />)}
      </section>
    </div>
  );
}

function Entry({ entry }: { entry: OutboxRow }) {
  const host = useHost()!;
  const c = entry.conflict;
  return (
    <div className="mt-2 flex flex-wrap items-baseline gap-2 text-xs">
      <strong>{entry.reg}</strong>
      <span className="mono">{entry.id}</span>
      <span className="muted">
        {entry.files} files, {formatBytes(entry.bytes)}, packed {entry.created_at}
        {entry.uploaded ? ", uploaded" : ", not uploaded"}
        {entry.attempts > 0 ? `, ${entry.attempts} attempts` : ""}
      </span>
      {entry.last_error ? <span className="text-warn">last error: {entry.last_error}</span> : null}
      {c ? (
        <span className="text-bad">
          {c.reason}
          {c.conflict_id ? `, kept on the platform as ${c.conflict_id}` : ", local copy only"}
        </span>
      ) : null}
      <button className="btn btn-sm" onClick={() => host.openPath(entry.path)}>
        Show folder
      </button>
    </div>
  );
}
